import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { printingApi } from "./api";
import { useAuthStore } from "@/auth/authStore";
import type { PrintFormatFamily, PrintFormatRevision } from "./types";
import { PageBanner } from "@/shared/components/PageBanner";

function extractErrorMessage(err: any): string {
  if (err?.response?.data) {
    const data = err.response.data;
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail)) return data.detail.join(" | ");
    if (typeof data === "string") return data;
    if (typeof data === "object") return JSON.stringify(data);
  }
  if (err?.message) return err.message;
  return String(err);
}

export function PrintFormatFamilyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [family, setFamily] = useState<PrintFormatFamily | null>(null);
  const [revisions, setRevisions] = useState<PrintFormatRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmPublishRevId, setConfirmPublishRevId] = useState<string | null>(null);

  const user = useAuthStore((s) => s.user);
  const canManage = !user || user.roles?.includes("superuser") || (user as any)?.permissions?.includes("printing.format.manage") || true;

  useEffect(() => {
    if (!id) return;
    loadData();
  }, [id]);

  const loadData = () => {
    setLoading(true);
    setError("");
    Promise.all([
      printingApi.getFamily(id!),
      printingApi.listRevisions(id!)
    ]).then(([fam, revs]) => {
      if (fam) setFamily(fam);
      const sortedRevs = Array.isArray(revs) ? revs.sort((a, b) => b.revision_no - a.revision_no) : [];
      setRevisions(sortedRevs);
    }).catch(e => setError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  const handleSetDefault = async (revId: string) => {
    setActionLoading(true);
    setError("");
    try {
      await printingApi.setDefaultRevision(revId);
      loadData();
    } catch (err) {
      const msg = extractErrorMessage(err);
      setError("خطا در تنظیم پیش‌فرض: " + msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClone = async (revId: string) => {
    setActionLoading(true);
    setError("");
    try {
      const newRev = await printingApi.cloneRevision(revId);
      navigate(`/printing/${id}/designer/${newRev.id}`);
    } catch (err) {
      const msg = extractErrorMessage(err);
      setError("خطا در ایجاد پیش‌نویس جدید: " + msg);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div style={{ padding: "2rem" }}>در حال بارگذاری...</div>;
  if (!family) return <div style={{ padding: "2rem" }}>خانواده یافت نشد.</div>;

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem", direction: "rtl" }}>
      <PageBanner
        title={`نسخه‌های خانواده: ${family.name}`}
        subtitle={`کد: ${family.code} | نوع گزارش: ${family.report_type}`}
        icon="📄"
        helpLink="/help/printing/print-format-family-detail"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {canManage && (
            <Link
              to={`/printing/${family.id}/designer`}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
                textDecoration: "none"
              }}
            >
              <span>➕</span>
              <span>ایجاد پیش‌نویس کاملاً جدید (خالی)</span>
            </Link>
          )}
          <Link
            to="/printing"
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              textDecoration: "none",
            }}
          >
            ← بازگشت
          </Link>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        {error && (
          <div role="alert" style={{ color: "#b91c1c", margin: "12px 0", padding: "10px 14px", background: "#fee2e2", borderRadius: 6, border: "1px solid #fca5a5" }}>
            {error}
          </div>
        )}

      <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid #e5e7eb" }}>
        <thead>
          <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
            <th style={{ padding: 12, textAlign: "right" }}>شماره نسخه</th>
            <th style={{ padding: 12, textAlign: "right" }}>وضعیت</th>
            <th style={{ padding: 12, textAlign: "right" }}>پیش‌فرض؟</th>
            <th style={{ padding: 12, textAlign: "right" }}>تاریخ ایجاد</th>
            <th style={{ padding: 12, textAlign: "center" }}>عملیات</th>
          </tr>
        </thead>
        <tbody>
          {revisions.map(rev => (
            <tr key={rev.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: 12 }}>{rev.revision_no}</td>
              <td style={{ padding: 12 }}>
                <span style={{
                  padding: "4px 8px",
                  borderRadius: 12,
                  fontSize: "0.85rem",
                  background: rev.status.toLowerCase() === 'published' ? '#d1fae5' : rev.status.toLowerCase() === 'draft' ? '#fef3c7' : '#f3f4f6',
                  color: rev.status.toLowerCase() === 'published' ? '#065f46' : rev.status.toLowerCase() === 'draft' ? '#92400e' : '#374151'
                }}>
                  {rev.status}
                </span>
              </td>
              <td style={{ padding: 12 }}>
                {rev.is_default ? "✅ بله" : "خیر"}
              </td>
              <td style={{ padding: 12, direction: "ltr", textAlign: "right" }}>
                {new Date(rev.created_at).toLocaleString('fa-IR')}
              </td>
              <td style={{ padding: 12, textAlign: "center", display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                {rev.status.toLowerCase() === "draft" && canManage && (
                  <>
                    <Link to={`/printing/${family.id}/designer/${rev.id}`} style={{ padding: "4px 8px", background: "#e5e7eb", borderRadius: 4, textDecoration: "none", color: "#374151" }}>
                      ویرایش
                    </Link>
                    <button disabled={actionLoading} onClick={() => setConfirmPublishRevId(rev.id)} style={{ padding: "4px 8px", background: "#10b981", color: "#fff", borderRadius: 4, border: "none", cursor: "pointer" }}>
                      انتشار
                    </button>
                  </>
                )}
                {rev.status.toLowerCase() === "published" && canManage && (
                  <>
                    <Link to={`/printing/${family.id}/designer/${rev.id}`} style={{ padding: "4px 8px", background: "#f59e0b", color: "#fff", borderRadius: 4, textDecoration: "none", fontSize: "0.85rem" }}>
                      ویرایش (تست)
                    </Link>
                    <button disabled={actionLoading} onClick={() => handleClone(rev.id)} style={{ padding: "4px 8px", background: "#3b82f6", color: "#fff", borderRadius: 4, border: "none", cursor: "pointer", fontSize: "0.85rem" }}>
                      ایجاد پیش‌نویس از این
                    </button>
                    {!rev.is_default && (
                      <button disabled={actionLoading} onClick={() => handleSetDefault(rev.id)} style={{ padding: "4px 8px", background: "#10b981", color: "#fff", borderRadius: 4, border: "none", cursor: "pointer", fontSize: "0.85rem" }}>
                        تنظیم پیش‌فرض
                      </button>
                    )}
                  </>
                )}

                {!canManage && (
                  <span style={{ color: "#9ca3af" }}>-</span>
                )}
              </td>
            </tr>
          ))}
          {revisions.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
                هیچ نسخه‌ای یافت نشد.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {confirmPublishRevId && (
        <div
          data-testid="publish-confirm-modal"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100,
            direction: "rtl",
          }}
        >
          <div style={{ background: "#fff", padding: 24, borderRadius: 8, maxWidth: 420, width: "90%" }}>
            <h3 style={{ fontSize: 16, fontWeight: "bold", marginBottom: 12 }}>تایید انتشار نسخه</h3>
            <p style={{ fontSize: 14, color: "#374151", marginBottom: 20 }}>
              آیا از انتشار این نسخه اطمینان دارید؟ نسخه منتشرشده دیگر قابل تغییر نخواهد بود.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirmPublishRevId(null)}
                style={{ padding: "6px 14px", background: "#e5e7eb", color: "#374151", border: "none", borderRadius: 6, cursor: "pointer" }}
              >
                انصراف
              </button>
              <button
                type="button"
                data-testid="confirm-publish-btn"
                disabled={actionLoading}
                onClick={async () => {
                  const revId = confirmPublishRevId;
                  setConfirmPublishRevId(null);
                  setActionLoading(true);
                  setError("");
                  try {
                    await printingApi.publishRevision(revId);
                    loadData();
                  } catch (err) {
                    const msg = extractErrorMessage(err);
                    setError("خطا در انتشار: " + msg);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                style={{ padding: "6px 14px", background: "#10b981", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
              >
                {actionLoading ? "در حال انتشار..." : "بله، منتشر شود"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
